import type { AnchorHTMLAttributes, ButtonHTMLAttributes, FC, ReactNode } from 'react'

import Link from 'next/link'
import { cn } from '@/lib/utils'

type ButtonVariant = 'primary' | 'outline' | 'dark' | 'light'
type ButtonSize = 'sm' | 'md' | 'lg'

interface ButtonStyleOptions {
    variant?: ButtonVariant
    size?: ButtonSize
    className?: string
}

const FILL = 'before:absolute before:inset-0 before:-z-10 before:origin-left rtl:before:origin-right before:scale-x-0 before:transition-transform before:duration-500 before:ease-(--ease-soft) hover:before:scale-x-100 focus-visible:before:scale-x-100'

const VARIANTS: Record<ButtonVariant, string> = {
    primary: cn('bg-primary text-primary-foreground before:bg-amber-deep', FILL),
    outline: cn('border border-foreground/70 text-foreground hover:text-background focus-visible:text-background before:bg-foreground', FILL),
    dark: cn('bg-foreground text-background before:bg-amber-deep', FILL),
    light: cn('bg-background text-foreground hover:text-background focus-visible:text-background before:bg-foreground', FILL)
}

const SIZES: Record<ButtonSize, string> = {
    sm: 'h-10 px-4 text-[0.8125rem]',
    md: 'h-12 px-6 text-sm',
    lg: 'h-14 px-8 text-[0.9375rem]'
}

export const buttonClasses = ({ variant = 'primary', size = 'md', className }: ButtonStyleOptions = {}): string => cn(
    'group/button relative isolate inline-flex shrink-0 cursor-pointer items-center justify-center gap-2.5 overflow-hidden rounded-sm font-medium tracking-[0.01em] whitespace-nowrap transition-[color,border-color,opacity] duration-500 ease-(--ease-soft)',
    'focus-visible:outline-2 focus-visible:outline-offset-3 focus-visible:outline-amber disabled:pointer-events-none disabled:opacity-45',
    VARIANTS[variant],
    SIZES[size],
    className
)

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement>, ButtonStyleOptions {
    children: ReactNode
}

export const Button: FC<ButtonProps> = ({ variant, size, className, type = 'button', children, ...rest }) => (
    <button type={type} className={buttonClasses({ variant, size, className })} {...rest}>
        {children}
    </button>
)

interface ButtonLinkProps extends Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href'>, ButtonStyleOptions {
    href: string
    children: ReactNode
}

export const ButtonLink: FC<ButtonLinkProps> = ({ href, variant, size, className, children, ...rest }) => {
    const classes = buttonClasses({ variant, size, className })

    if (/^(https?:|tel:|mailto:)/.test(href)) {
        const external = href.startsWith('http')

        return (
            <a href={href} className={classes} target={external ? '_blank' : undefined} rel={external ? 'noopener noreferrer' : undefined} {...rest}>
                {children}
            </a>
        )
    }

    return (
        <Link href={href} className={classes} {...rest}>
            {children}
        </Link>
    )
}
